import React from 'react'
import { Button } from '@/atoms/Button'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionLG, TitleLG, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BeforeYouLeaveProvider } from '@/contexts/BeforeYouLeaveContext'
import { BeforeYouLeaveModal } from '@/molecules/BeforeYouLeaveModal'
import { AngelFundingLink } from '@/molecules/InvestLink'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'

export const SMALL_INVESTMENT_BANNER_INFO = {
  // TODO: leaving this as an example for next investment round, which is likely to be Sketch. Values will need to be updated.
  notSketchWontAccidentallyShow: {
    name: 'Sketch',
    bannerBackgroundImg: '/v1747252453/angel-studios/banners/last-rodeo-investment-banner',
    focusBackgroundImg: '/v1747252453/angel-studios/banners/last-rodeo-neal',
    ctaPath: paths.external.invest.lastRodeo,
    logoCloudinaryPath: '/v1739566078/last-rodeo-logo-white_t34gvh',
  },
} as const

interface SmallInvestmentBannerProps {
  slug: keyof typeof SMALL_INVESTMENT_BANNER_INFO
}

export const SmallInvestmentBanner: React.FC<SmallInvestmentBannerProps> = ({ slug }) => {
  const { t } = useTranslate('common')
  const { innerWidth } = useWindowSize()
  const bannerInfo = SMALL_INVESTMENT_BANNER_INFO[slug]

  if (!bannerInfo) {
    return null
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-white/30">
      <ResponsiveBackgroundImage
        responsiveAR={{
          xs: 396 / 172,
          sm: 428 / 172,
          md: 548 / 172,
          lg: 1060 / 152,
          xl: 1600 / 152,
          '2xl': 1600 / 152,
          '3xl': 1600 / 152,
        }}
        src={bannerInfo.bannerBackgroundImg}
      />
      <ResponsiveBackgroundImage
        src={bannerInfo.focusBackgroundImg}
        responsiveAR={{
          xs: 3,
          sm: 3,
          md: 1,
          lg: 1,
          xl: 1,
          '2xl': 1,
          '3xl': 1,
        }}
        gravity="g_face"
        backgroundPosition={innerWidth > 768 ? 'bg-center' : 'bg-right'}
        className="md:mx-auto md:max-w-[400px]"
      />
      <div className="relative flex items-center justify-between p-5 md:mx-auto md:max-w-[862px] lg:max-w-[942px]">
        <div className="hidden text-center md:block">
          <TitleLG weight="semibold" color="warm-03" className="mb-3">
            {t('investToday', 'Invest Today!')}
          </TitleLG>
          <Image src={bannerInfo.logoCloudinaryPath} alt={bannerInfo.name} width={212} height={212} />
        </div>
        <div className="max-w-[212px] md:text-center">
          <TitleMD color="warm-03" weight="bold" className="mb-2 md:hidden">
            {t('investToday', 'Invest Today!')}
          </TitleMD>
          <CaptionLG color="white" className="mb-5">
            {t('supportProjectTheatricalRelease', 'Support {{project}} theatrical release now.', {
              project: bannerInfo.name,
            })}
          </CaptionLG>
          <BeforeYouLeaveProvider>
            <BeforeYouLeaveModal />
            <AngelFundingLink
              linkContext="Project Journey"
              href={bannerInfo.ctaPath}
              leavingContinueLabel={t('continue', 'Continue')}
              leavingMessage={t(
                'youAreNowLeavingThisWebsiteToEnterAFINRARegulatedPortal',
                'You are now leaving this website to enter a FINRA regulated portal.',
              )}
            >
              <Button size="sm" variant="offwhite" className="inline-flex items-center gap-1">
                {t('learnMore', 'Learn More')}
                <ExternalLinkIcon color="white" size={20} />
              </Button>
            </AngelFundingLink>
          </BeforeYouLeaveProvider>
        </div>
      </div>
    </div>
  )
}
